import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, Roadmap, Module, Lesson, Tournament } from "../lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { useToast } from "../hooks/use-toast";
import {
  CheckCircle2,
  Circle,
  Lock,
  PlayCircle,
  ChevronDown,
  ChevronRight,
  Trophy,
  Target,
  Flame,
  BookOpen,
  ArrowRight,
  Sword,
  Users,
  Loader2,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

export default function IthopuCurriculum() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [roadmap, setRoadmap] = useState<Roadmap | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(
    new Set(),
  );
  const [tournaments, setTournaments] = useState<Map<string, Tournament>>(
    new Map(),
  );
  const [joiningTournament, setJoiningTournament] = useState<string | null>(
    null,
  );

  useEffect(() => {
    loadRoadmap();
  }, [location.key]); // Reload whenever user navigates to this page

  const loadTournaments = async (roadmap: Roadmap) => {
    try {
      const tournamentMap = new Map<string, Tournament>();
      const uniqueTournamentIds = new Set<string>();
      const lessonToTournamentId = new Map<string, string>();

      // 1. Collect unique tournament IDs and associate lessons
      if (roadmap.modules) {
        for (const module of roadmap.modules) {
          if (module.lessons) {
            for (const lesson of module.lessons) {
              if (lesson.lesson_type === "challenge" && lesson.tournament_id) {
                uniqueTournamentIds.add(lesson.tournament_id);
                lessonToTournamentId.set(lesson.id, lesson.tournament_id);
              }
            }
          }
        }
      }

      // 2. Fetch tournaments in parallel (deduplicated)
      const fetchedTournaments = new Map<string, Tournament>();
      await Promise.all(
        Array.from(uniqueTournamentIds).map(async (id) => {
          try {
            const tournament = await api.getTournament(id);
            fetchedTournaments.set(id, tournament);
          } catch (err) {
            console.error(`Failed to load tournament ${id}:`, err);
            // Continue even if one fails
          }
        }),
      );

      // 3. Map results back to lessons
      lessonToTournamentId.forEach((tournamentId, lessonId) => {
        const tournament = fetchedTournaments.get(tournamentId);
        if (tournament) {
          tournamentMap.set(lessonId, tournament);
        }
      });

      setTournaments(tournamentMap);
    } catch (err) {
      console.error("Failed to load tournaments:", err);
      toast({
        title: "Warning",
        description: "Some tournament information could not be loaded.",
        variant: "default",
      });
    }
  };

  const loadRoadmap = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all roadmaps
      const roadmaps = await api.getRoadmaps();

      // Find Ithopu Addition & Subtraction curriculum
      const ithopuRoadmap = roadmaps.find(
        (r) => r.slug === "addition-subtraction-mastery",
      );

      if (!ithopuRoadmap) {
        setError("Addition & Subtraction Ithopu curriculum not found");
        return;
      }

      // Get full roadmap details with modules and lessons
      const fullRoadmap = await api.getRoadmap(ithopuRoadmap.id);
      setRoadmap(fullRoadmap);

      // Load tournaments for challenge lessons
      await loadTournaments(fullRoadmap);

      // Expand first module by default
      if (fullRoadmap.modules && fullRoadmap.modules.length > 0) {
        setExpandedModules(new Set([fullRoadmap.modules[0].id]));
      }
    } catch (err) {
      console.error("Failed to load roadmap:", err);
      setError(err instanceof Error ? err.message : "Failed to load roadmap");
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (moduleId: string) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(moduleId)) {
      newExpanded.delete(moduleId);
    } else {
      newExpanded.add(moduleId);
    }
    setExpandedModules(newExpanded);
  };

  const getLessonTypeIcon = (lessonType?: string) => {
    switch (lessonType) {
      case "learn":
        return <BookOpen className="w-4 h-4 text-blue-500" />;
      case "forge":
        return <Flame className="w-4 h-4 text-orange-500" />;
      case "challenge":
        return <Trophy className="w-4 h-4 text-purple-500" />;
      default:
        return <BookOpen className="w-4 h-4 text-gray-500" />;
    }
  };

  const getLessonStatusIcon = (lesson: Lesson) => {
    if (!user) {
      return <Lock className="w-5 h-5 text-gray-400" />;
    }

    const status = lesson.progress?.status || "locked";

    switch (status) {
      case "completed":
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case "in_progress":
        return <PlayCircle className="w-5 h-5 text-blue-500" />;
      case "unlocked":
        return <Circle className="w-5 h-5 text-yellow-500" />;
      case "locked":
      default:
        return <Lock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getLessonStatusColor = (lesson: Lesson) => {
    if (!user) return "bg-gray-100 border-gray-300";

    const status = lesson.progress?.status || "locked";

    switch (status) {
      case "completed":
        return "bg-green-50 border-green-500";
      case "in_progress":
        return "bg-blue-50 border-blue-500";
      case "unlocked":
        return "bg-yellow-50 border-yellow-500";
      case "locked":
      default:
        return "bg-gray-100 border-gray-300";
    }
  };

  const getLessonStatusText = (lesson: Lesson) => {
    if (!user) return "Locked";

    const status = lesson.progress?.status || "locked";

    switch (status) {
      case "completed":
        return "Completed";
      case "in_progress":
        return "In Progress";
      case "unlocked":
        return "Available";
      case "locked":
      default:
        return "Locked";
    }
  };

  const handleLessonClick = (lesson: Lesson) => {
    if (!user) {
      navigate("/login");
      return;
    }

    const status = lesson.progress?.status || "locked";
    if (status === "locked") {
      return; // Can't click locked lessons
    }

    navigate(`/lesson/${lesson.id}?returnTo=/arena/ithopu-curriculum`);
  };

  const handleJoinTournament = async (
    event: React.MouseEvent,
    tournamentId: string,
  ) => {
    event.stopPropagation(); // Prevent lesson click

    if (!user) {
      navigate("/login");
      return;
    }

    try {
      setJoiningTournament(tournamentId);
      await api.joinTournament(tournamentId);

      toast({
        title: "🎉 Tournament Joined!",
        description:
          "You've successfully enrolled in the tournament. Good luck!",
      });

      // Refresh tournament data to update enrollment status
      await loadRoadmap();

      // Navigate to tournament
      navigate(`/tournament?tournamentId=${tournamentId}`);
    } catch (err) {
      console.error("Failed to join tournament:", err);
      toast({
        title: "Failed to Join Tournament",
        description:
          "You may already be enrolled or the tournament may be full.",
        variant: "destructive",
      });
    } finally {
      setJoiningTournament(null);
    }
  };

  const getModuleProgress = (module: Module) => {
    if (!module.lessons || module.lessons.length === 0) return 0;

    const completedLessons = module.lessons.filter(
      (l) => l.progress?.status === "completed",
    ).length;

    return (completedLessons / module.lessons.length) * 100;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading curriculum...</p>
        </div>
      </div>
    );
  }

  if (error || !roadmap) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="text-red-600">Error</CardTitle>
            <CardDescription>
              {error || "Failed to load curriculum"}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Detailed curriculum view
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-12">
      {/* Header */}
      <div className="bg-gray-900 dark:bg-gray-800 text-white py-12 mb-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Button
              variant="ghost"
              className="text-gray-400 hover:text-white mb-6 p-0 hover:bg-transparent"
              onClick={() => navigate("/arena")}
            >
              <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
              Back to Arena
            </Button>
            <div className="text-center">
              <div className="text-6xl mb-4 flex justify-center">
                {roadmap.icon}
              </div>
              <h1 className="text-4xl font-bold mb-4">{roadmap.name}</h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-6">
                {roadmap.description}
              </p>

              {roadmap.objective && (
                <div className="flex items-start gap-2 bg-white/10 rounded-lg p-4 text-left justify-center inline-flex">
                  <Target className="w-5 h-5 mt-1 flex-shrink-0" />
                  <p className="text-sm leading-relaxed">{roadmap.objective}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modules */}
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto space-y-6">
          {roadmap.modules?.map((module) => {
            const isExpanded = expandedModules.has(module.id);
            const progress = getModuleProgress(module);
            const completedLessons =
              module.lessons?.filter((l) => l.progress?.status === "completed")
                .length || 0;
            const totalLessons = module.lessons?.length || 0;

            return (
              <Card
                key={module.id}
                className="overflow-hidden border-2 hover:shadow-lg transition-all duration-300"
              >
                <CardHeader
                  className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => toggleModule(module.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="text-4xl">{module.icon}</div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <CardTitle className="text-xl text-gray-900 dark:text-white">
                            Module {module.module_number}: {module.name}
                          </CardTitle>
                          {progress === 100 && (
                            <Badge
                              variant="default"
                              className="bg-green-500 hover:bg-green-600"
                            >
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Completed
                            </Badge>
                          )}
                        </div>
                        <CardDescription className="text-sm mb-3 text-gray-600 dark:text-gray-400">
                          {module.description}
                        </CardDescription>

                        {/* Progress Bar */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-600 dark:text-gray-400">
                              {completedLessons} of {totalLessons} lessons
                              completed
                            </span>
                            <span className="font-semibold text-blue-600 dark:text-blue-400">
                              {Math.round(progress)}%
                            </span>
                          </div>
                          <Progress value={progress} className="h-2" />
                        </div>
                      </div>
                    </div>
                    <div>
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                      )}
                    </div>
                  </div>
                </CardHeader>

                {isExpanded && (
                  <CardContent className="pt-0">
                    <div className="space-y-3">
                      {module.lessons?.map((lesson) => {
                        const isLocked =
                          lesson.progress?.status === "locked" ||
                          !lesson.progress;
                        const isClickable = !isLocked && user;

                        return (
                          <div
                            key={lesson.id}
                            className={`
                              border-2 rounded-lg p-4 transition-all duration-300
                              ${getLessonStatusColor(lesson)}
                              ${isClickable ? "cursor-pointer hover:shadow-md hover:scale-[1.02]" : "cursor-not-allowed opacity-75"}
                            `}
                            onClick={() => handleLessonClick(lesson)}
                          >
                            <div className="flex items-center gap-3">
                              {/* Status Icon */}
                              <div className="flex-shrink-0">
                                {getLessonStatusIcon(lesson)}
                              </div>

                              {/* Lesson Type Icon */}
                              <div className="flex-shrink-0">
                                {getLessonTypeIcon(lesson.lesson_type)}
                              </div>

                              {/* Lesson Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-semibold text-sm text-gray-900 dark:text-white">
                                    {lesson.title}
                                  </h4>
                                  <Badge
                                    variant="outline"
                                    className="text-xs capitalize"
                                  >
                                    {lesson.lesson_type || "learn"}
                                  </Badge>
                                </div>
                                <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-1">
                                  {lesson.description}
                                </p>

                                {/* Progress for in-progress lessons */}
                                {lesson.progress?.status === "in_progress" &&
                                  lesson.progress.problems_attempted > 0 && (
                                    <div className="mt-2 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                                      <span>
                                        {lesson.progress.problems_correct}/
                                        {lesson.progress.problems_attempted}{" "}
                                        correct
                                      </span>
                                      <span>•</span>
                                      <span>
                                        {lesson.progress.current_accuracy?.toFixed(
                                          0,
                                        )}
                                        % accuracy
                                      </span>
                                    </div>
                                  )}

                                {/* Tournament info for completed challenge lessons */}
                                {lesson.lesson_type === "challenge" &&
                                  lesson.progress?.status === "completed" &&
                                  tournaments.get(lesson.id) && (
                                    <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 rounded-lg">
                                      <div className="flex items-center gap-2 mb-2">
                                        <Trophy className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                        <h5 className="text-sm font-semibold text-purple-900 dark:text-purple-100">
                                          {tournaments.get(lesson.id)?.name}
                                        </h5>
                                      </div>
                                      <p className="text-xs text-purple-700 dark:text-purple-300 mb-2 leading-relaxed">
                                        {
                                          tournaments.get(lesson.id)
                                            ?.description
                                        }
                                      </p>
                                      <div className="flex items-center gap-3 text-xs text-purple-600 dark:text-purple-400 mb-2">
                                        <div className="flex items-center gap-1">
                                          <Users className="w-3 h-3" />
                                          <span>
                                            {tournaments.get(lesson.id)
                                              ?.participant_count || 0}{" "}
                                            enrolled
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                          <Sword className="w-3 h-3" />
                                          <span className="capitalize">
                                            {
                                              tournaments.get(lesson.id)
                                                ?.difficulty
                                            }
                                          </span>
                                        </div>
                                      </div>
                                      <Button
                                        size="sm"
                                        className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                                        onClick={(e) =>
                                          handleJoinTournament(
                                            e,
                                            tournaments.get(lesson.id)!.id,
                                          )
                                        }
                                        disabled={
                                          joiningTournament ===
                                          tournaments.get(lesson.id)?.id
                                        }
                                      >
                                        {joiningTournament ===
                                        tournaments.get(lesson.id)?.id ? (
                                          <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Joining...
                                          </>
                                        ) : tournaments.get(lesson.id)
                                            ?.is_enrolled ? (
                                          <>
                                            <Trophy className="w-4 h-4 mr-2" />
                                            Continue Tournament
                                          </>
                                        ) : (
                                          <>
                                            <Sword className="w-4 h-4 mr-2" />
                                            Join Tournament
                                          </>
                                        )}
                                      </Button>
                                    </div>
                                  )}
                              </div>

                              {/* Status Badge */}
                              <div className="flex-shrink-0">
                                <Badge variant="outline" className="text-xs">
                                  {getLessonStatusText(lesson)}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
